import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { getConfigVacunas, getConfigSecciones, getConfigCatalogos, getVacunadores } from "../../api/pai/paiApi";

/**
 * usePaiConfig
 * Carga los catálogos dinámicos del módulo PAI desde la BD:
 *   - paiData:     equivalente al antiguo PAI_DATA (vacunas × grupo × campos)
 *   - patientData: equivalente al antiguo PATIENT_DATA (secciones del paciente × campos)
 *   - catalogos:   países, departamentos, municipios y aseguradoras
 *
 * Sigue el patrón de hooks del proyecto (ver useConsecutivo.js, useGroup.js).
 * La lógica asíncrona y los toasts están encapsulados aquí;
 * el componente solo consume { paiData, patientData, catalogos, configLoading, reloadConfig }.
 */
export const usePaiConfig = () => {
  const [paiData, setPaiData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [vacunadores, setVacunadores] = useState([]);
  const [catalogos, setCatalogos] = useState({
    paises: [],
    departamentos: [],
    municipios: [],
    aseguradoras: [],
  });
  const [configLoading, setConfigLoading] = useState(true);

  // async-parallel: carga catálogos en paralelo (Promise.all)
  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const [vacunas, secciones, vacs, cats] = await Promise.all([
        getConfigVacunas(),
        getConfigSecciones(),
        getVacunadores(),
        getConfigCatalogos(),
      ]);
      setPaiData(vacunas);
      setPatientData(secciones);
      setVacunadores(vacs);
      setCatalogos(cats || { paises: [], departamentos: [], municipios: [], aseguradoras: [] });
    } catch (err) {
      console.error("[usePaiConfig] Error cargando configuración:", err);
      toast.error("Error cargando la configuración del formulario PAI");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    paiData,
    patientData,
    vacunadores,
    catalogos,
    configLoading,
    reloadConfig: loadConfig,
  };
};
